/**
 * The map's entity-type chips (All / Campsites / Free camps / Caravan / Stays).
 *
 * They used to filter only the map pins: the list beside the map and the
 * "N results" count stayed put, so picking "Free camps" looked broken. The
 * chip state now lives in HomeClient and filters pins, list and count alike;
 * this module is the one definition all three share.
 */
import type { Entity } from '../types';

export type MapTypeKey = 'all' | 'campsites' | 'free' | 'caravan' | 'accom';

export const MAP_TYPE_CHIPS: { key: MapTypeKey; label: string; noun: [string, string]; types: string[] | null }[] = [
  { key: 'all', label: 'All', noun: ['result', 'results'], types: null },
  { key: 'campsites', label: 'Campsites', noun: ['campsite', 'campsites'], types: ['campsite', 'campground', 'camping'] },
  { key: 'free', label: 'Free camps', noun: ['free camp', 'free camps'], types: ['free_camping'] },
  { key: 'caravan', label: 'Caravan', noun: ['caravan park', 'caravan parks'], types: ['caravan_park'] },
  { key: 'accom', label: 'Stays', noun: ['stay', 'stays'], types: ['accom', 'accommodation'] },
];

export function entityTypeOf(entity: Entity): string {
  const e = entity as Entity & { entityType?: string };
  return String(e.entityType || e.entity_type || '').toLowerCase();
}

/** Does `entity` pass the chip `key`? Unknown keys and 'all' pass everything. */
export function matchesMapType(entity: Entity, key: string): boolean {
  if (!key || key === 'all') return true;
  const chip = MAP_TYPE_CHIPS.find((c) => c.key === key);
  if (!chip?.types) return true;
  return chip.types.includes(entityTypeOf(entity));
}

/** "free camps" / "caravan park" — the count label for an active chip; null for 'all'. */
export function mapTypeNoun(key: string, n: number): string | null {
  if (!key || key === 'all') return null;
  const chip = MAP_TYPE_CHIPS.find((c) => c.key === key);
  if (!chip) return null;
  return n === 1 ? chip.noun[0] : chip.noun[1];
}
